/**
 * CDP SDK News Bundle
 * Core SDK + News Plugin for news sites and blogs
 *
 * Usage:
 * <script src="cdp-news.global.js"></script>
 * <script>
 *   cdp.init('your-write-key', {
 *     apiHost: 'https://your-api.com',
 *     debug: true
 *   });
 *
 *   // News plugin is automatically registered and will:
 *   // - Detect article pages
 *   // - Track Article Viewed, Engaged, Read events
 *   // - Track scroll milestones
 *   // - Track reading sessions on page unload
 *   // - Track share button clicks
 * </script>
 *
 * Custom configuration:
 * <script>
 *   // Configure before init
 *   cdp.configureNews({
 *     siteName: 'My News Site',
 *     siteId: 'my-site',
 *     selectors: {
 *       articlePage: '.my-article',
 *       articleTitle: 'h1.title',
 *       articleContent: '.content',
 *       articleAuthor: '.author',
 *       articleCategory: '.category'
 *     },
 *     categoryMap: {
 *       'noticias': 'News',
 *       'deportes': 'Sports'
 *     },
 *     engageThreshold: { scrollPercent: 25, timeSeconds: 15 },
 *     readThreshold: { scrollPercent: 50, timeSeconds: 25 }
 *   });
 *
 *   cdp.init('your-write-key', { apiHost: 'https://your-api.com' });
 * </script>
 */

import { cdp } from '../core';
import { warn } from '../core/utils';
import type { SiteProfile } from '../core/types';
import { createNewsPlugin, type NewsPluginConfig } from '../plugins/news';
import { createRecoPlugin } from '../plugins/reco';
import { createNewsletterPlugin } from '../plugins/newsletter';
import { createPushPlugin, capturePushClickLink, reportPushLink } from '../plugins/push';
import { readPreloadedProfile } from '../core/site-config';
import { getStorage } from '../core/storage';
import { STORAGE_KEYS } from '../core/types';

// Re-export core types
export type {
  CDPCoreAPI,
  CDPCoreConfig,
  CDPPlugin,
  ConsentPreferences,
  ConsentType,
  EventContext,
  EventPayload,
  SessionData,
  DeviceFingerprint,
} from '../core';

// Re-export news plugin types
export type {
  NewsPluginConfig,
  ArticleMetadata,
  ArticleSelectors,
  ReadingThreshold,
  ArticleViewedProperties,
  ArticleEngagedProperties,
  ArticleReadProperties,
  ArticleReadingSessionProperties,
  ArticleScrollMilestoneProperties,
  ArticleSharedProperties,
} from '../plugins/news';

// Re-export plugin utilities
export {
  definePlugin,
  $,
  $$,
  getText,
  getAttr,
  getData,
  matches,
  closest,
  addListener,
  observeDOM,
  observeIntersection,
  parseUrl,
  getQueryParam,
  urlMatches,
  getJsonLd,
  getOpenGraph,
  getMeta,
  getScrollPercent,
  getElementVisibility,
  debounce,
  throttle,
  waitFor,
  waitForElement,
} from '../plugins/base';

// Re-export news utilities
export {
  NEWS_EVENTS,
  isArticlePage,
  extractArticleMetadata,
  getPageType,
  DEFAULT_NEWS_CONFIG,
  DEFAULT_ARTICLE_SELECTORS,
  DEFAULT_ENGAGE_THRESHOLD,
  DEFAULT_READ_THRESHOLD,
  DEFAULT_SCROLL_MILESTONES,
} from '../plugins/news';

// =============================================================================
// NEWS CONFIGURATION
// =============================================================================

// Store news config for lazy plugin creation
let newsConfig: NewsPluginConfig = {};
let newsPluginRegistered = false;

// Optional inline Site Profile override (normally the profile is remote-managed;
// this is for testing or an emergency override without a dashboard change).
let inlineSiteProfile: Partial<SiteProfile> | null = null;

/**
 * Configure the news plugin before initialization
 * This must be called before cdp.init()
 */
function configureNews(config: NewsPluginConfig): void {
  newsConfig = { ...newsConfig, ...config };
}

/**
 * Inline-override the remote Site Profile (selectors, URL taxonomy, event
 * toggles, consent, privacy). Merged OVER the remote profile, so it wins. Must
 * be called before cdp.init(). Optional — most sites rely on the dashboard.
 */
function configureSite(profile: Partial<SiteProfile>): void {
  inlineSiteProfile = { ...inlineSiteProfile, ...profile };
}

/**
 * Register the news plugin (called automatically on init)
 */
/** The profile's push block before core.init() resolves it: the loader's pre-fetched profile, else last visit's. */
function declaredPush(writeKey?: string): SiteProfile['push'] & { language?: string } {
  const pick = (p: SiteProfile) => ({ ...(p.push || {}), language: p.language });
  try {
    const pre = writeKey !== undefined ? readPreloadedProfile(writeKey) : null;
    if (pre) return pick(pre);
  } catch { /* ignore */ }
  try {
    const cached = getStorage<{ profile?: SiteProfile; writeKey?: string }>(STORAGE_KEYS.SITE_CONFIG);
    if (cached?.profile && (!writeKey || cached.writeKey === writeKey)) return pick(cached.profile);
  } catch { /* ignore */ }
  return {};
}

function ensureNewsPlugin(writeKey?: string): void {
  if (newsPluginRegistered) return;

  const newsPlugin = createNewsPlugin(newsConfig);
  cdp.use(newsPlugin);
  // Reco plugin — self-gates on the Site Profile carrying reco placements
  // (served in /v1/config). News sites get the news bundle, so it must live
  // here too, not only in the full bundle.
  cdp.use(createRecoPlugin());
  // Web push (monfri ОБ-Р20/Р32, 18.10): the notification invitation lived only in cdp-full, and the universal loader
  // gives a news site this bundle — a new news site's readers were never invited. Same gate as the full bundle: the
  // profile's push block names the site's Push app (and the owner's switch, push.enabled, is honoured by the plugin).
  const push = declaredPush(writeKey);
  if (push.appId) {
    cdp.use(createPushPlugin({ ...push }));
  }
  // The newsletter form under the article (monfri ОБ-Р33) — self-gates on the profile's newsletter block.
  cdp.use(createNewsletterPlugin());
  newsPluginRegistered = true;
}

// =============================================================================
// EXTENDED CDP API
// =============================================================================

// Wrap the init function to auto-register news plugin
const originalInit = cdp.init;

const extendedCdp = {
  ...cdp,

  // Override init to auto-register news plugin
  init(writeKey: string, options?: Parameters<typeof cdp.init>[1]): void {
    // A notification click's link parameters leave the address before anything reads it (as in the full bundle).
    capturePushClickLink();
    ensureNewsPlugin(writeKey);
    // Fold any inline Site Profile override into init options (inline wins).
    const merged = inlineSiteProfile
      ? {
          ...options,
          siteProfile: { ...inlineSiteProfile, ...(options?.siteProfile || {}) },
        }
      : options;
    originalInit(writeKey, merged);
    cdp.onInitialized(() => reportPushLink(cdp));
  },

  // News-specific methods
  configureNews,
  configureSite,
  createNewsPlugin,
};

// =============================================================================
// READY CALLBACK AND COMMAND QUEUE
// =============================================================================

type QueuedCommand = [string, ...unknown[]];
const readyCallbacks: Array<() => void> = [];
let isReady = false;

/**
 * Register a callback to run when CDP is ready
 */
function ready(callback: () => void): void {
  if (isReady) {
    callback();
  } else {
    readyCallbacks.push(callback);
  }
}

/**
 * Process any queued commands from before SDK loaded
 */
function processQueue(): void {
  if (typeof window === 'undefined') return;

  const win = window as unknown as { cdp?: { q?: QueuedCommand[] } };
  if (win.cdp && Array.isArray(win.cdp.q)) {
    const queue = win.cdp.q;
    for (const args of queue) {
      const [method, ...params] = args;
      if (method && typeof (finalCdp as Record<string, unknown>)[method] === 'function') {
        (finalCdp as Record<string, (...args: unknown[]) => unknown>)[method](...params);
      }
    }
    // Clear the queue
    win.cdp.q = [];
  }

  // Mark as ready and run callbacks
  isReady = true;
  for (const cb of readyCallbacks) {
    try {
      cb();
    } catch (e) {
      console.error('[CDP] Ready callback error:', e);
    }
  }
}

// Final CDP object with ready support
const finalCdp = {
  ...extendedCdp,
  ready,
};

// For IIFE bundle - explicitly set on window BEFORE exports
// This ensures window.cdp has all methods regardless of how bundler wraps exports
if (typeof window !== 'undefined') {
  // Save the queue BEFORE overwriting window.cdp
  const existingQueue = (window as unknown as { cdp?: { q?: QueuedCommand[] } }).cdp?.q;

  (window as unknown as { cdp: typeof finalCdp }).cdp = finalCdp;
  (window as unknown as { cdpNews: typeof finalCdp }).cdpNews = finalCdp;

  // Process any queued commands from the saved queue
  if (existingQueue && Array.isArray(existingQueue)) {
    for (const args of existingQueue) {
      const [method, ...params] = args;
      if (!method) continue;
      const fn = (finalCdp as Record<string, unknown>)[method];
      if (typeof fn === 'function') {
        try {
          (fn as (...a: unknown[]) => unknown).apply(finalCdp, params);
        } catch (e) {
          // A queued command that throws must not kill the rest of the queue.
          warn('Queued command failed:', method, e);
        }
      } else {
        // Never drop silently — see the twin comment in bundles/full.ts.
        warn(
          `Queued command "${method}" was dropped — this bundle does not implement it. ` +
            'The call had no effect.'
        );
      }
    }
  }

  // Mark as ready and run callbacks
  isReady = true;
  for (const cb of readyCallbacks) {
    try {
      cb();
    } catch (e) {
      console.error('[CDP] Ready callback error:', e);
    }
  }
}

// Export extended CDP
export { finalCdp as cdp };
export default finalCdp;

// Backward compatibility alias
export const cdpNews = finalCdp;
